/**
 * AuditorTabsBar — Phase 10 Part B UI for the multi-tab auditor workspace.
 *
 * Renders one pill per tab (up to MAX_TABS=5) with:
 *   • editable title (double-click → inline input)
 *   • close [X] with unsaved-prompt confirmation
 *   • state dot (green=score>=8, red=bounce, amber=loading, dim=empty)
 *   • pulse animation on background completion (justCompleted flag)
 *   • active tab: gold bottom border, others opacity 60%
 * A trailing "+ New Tab" button creates the next slot; disabled at max.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { MAX_TABS, useAuditorTabs } from "../contexts/AuditorTabsContext";

function stateDot(tab) {
  if (tab.loading) {
    return <Loader2 className="h-3 w-3 animate-spin text-amber-400" aria-hidden />;
  }
  if (tab.result?.bounced) {
    return <span className="h-2 w-2 rounded-full bg-red-500" aria-hidden />;
  }
  const score = tab.result && !tab.result.bounced ? parseFloat(tab.result.nexa_score ?? 0) : NaN;
  if (Number.isFinite(score) && score >= 8.0) {
    return <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden />;
  }
  return <span className="h-2 w-2 rounded-full bg-white/25" aria-hidden />;
}

function TabPill({ tab, isActive, onActivate, onClose, onRename }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tab.title);
  const inputRef = useRef(null);

  useEffect(() => setDraft(tab.title), [tab.title]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== tab.title) {
      onRename(draft.trim());
    } else {
      setDraft(tab.title);
    }
  };

  return (
    <div
      className={
        "group relative flex items-center gap-2 rounded-t-lg border-b-2 px-3 py-2 text-sm transition-all " +
        (isActive
          ? "border-[#D4AF37] bg-white/[0.06] text-white"
          : "border-transparent text-white/60 hover:text-white/85 hover:bg-white/[0.03]") +
        (tab.justCompleted && !isActive ? " animate-pulse ring-1 ring-[#D4AF37]/40" : "")
      }
      role="tab"
      aria-selected={isActive}
      data-testid={`auditor-tab-${tab.id}`}
      data-tab-active={isActive}
      data-tab-completed={tab.justCompleted}
    >
      <button
        type="button"
        onClick={onActivate}
        className="flex items-center gap-2 outline-none focus-visible:ring-1 focus-visible:ring-[#D4AF37]/70 rounded"
      >
        {stateDot(tab)}
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setDraft(tab.title);
                setEditing(false);
              }
            }}
            className="w-32 bg-transparent border border-white/15 rounded px-1 text-sm outline-none focus:border-[#D4AF37]/70"
            data-testid={`auditor-tab-title-input-${tab.id}`}
            maxLength={40}
          />
        ) : (
          <span
            onDoubleClick={(e) => {
              e.stopPropagation();
              onActivate();
              setEditing(true);
            }}
            className="select-none max-w-[160px] truncate"
            title={t("auditor.tabs.rename")}
            data-testid={`auditor-tab-title-${tab.id}`}
          >
            {tab.title}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label={t("common.close")}
        className="rounded p-0.5 text-white/40 hover:text-white/85"
        data-testid={`auditor-tab-close-${tab.id}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function AuditorTabsBar() {
  const { t } = useTranslation();
  const { tabs, activeTabId, createTab, closeTab, activateTab, renameTab } = useAuditorTabs();

  const handleClose = (tab) => {
    if (tab.prompt && tab.prompt.trim().length > 0) {
      const ok = window.confirm(t("auditor.tabs.close_confirm"));
      if (!ok) return;
    }
    closeTab(tab.id);
  };

  const handleNew = () => {
    if (tabs.length >= MAX_TABS) {
      toast.info(t("auditor.tabs.max_reached"));
      return;
    }
    createTab();
  };

  return (
    <div
      className="mb-4 flex flex-wrap items-end gap-1 border-b border-white/10"
      role="tablist"
      data-testid="auditor-tabs-bar"
    >
      {tabs.map((tab) => (
        <TabPill
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
          onActivate={() => activateTab(tab.id)}
          onClose={() => handleClose(tab)}
          onRename={(title) => renameTab(tab.id, title)}
        />
      ))}
      <button
        type="button"
        onClick={handleNew}
        aria-disabled={tabs.length >= MAX_TABS}
        className={
          "ml-1 mb-0.5 inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white/70 hover:text-white hover:border-white/25 transition-colors " +
          (tabs.length >= MAX_TABS ? "opacity-40 cursor-not-allowed" : "")
        }
        data-testid="auditor-tab-new-btn"
        aria-label={t("auditor.tabs.new")}
      >
        <Plus className="h-3.5 w-3.5" />
        {t("auditor.tabs.new")}
      </button>
    </div>
  );
}

export default AuditorTabsBar;
